'use client';

import React from 'react';
import Link from '@/components/atoms/link';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import SupportContactCard from '@/components/molecules/support-contact-card';
import type { NavItem } from '../../types';
import type { SupportContact } from '@/providers/support-contact/types';

interface MobileMenuProps {
  open: boolean;
  mainNavItems: NavItem[];
  onClose: () => void;
  /** Single intent: user tapped "Shop Products" — coordinator closes this drawer then opens products on afterLeave. */
  onShopProducts: () => void;
  /** Forwarded to the Drawer; fires after this drawer's leave transition completes. */
  afterLeave?: () => void;
  /** Guest-only auth entries (Sign In + Register Trade Account) rendered at the top of the nav. */
  authActions?: React.ReactNode;
  /** Studio-driven support contact; falls back to constants inside the card when omitted. */
  supportContact?: SupportContact;
  // Disclosure wiring for the shared products dialog panel.
  productsMenuOpen: boolean;
  productsMenuId: string;
}

// EWM-295 — mobile navigation drawer. Built on the shared Drawer organism so it
// inherits the overlay, X-button close, focus-trap, focus-return and responsive
// width (full-width on mobile, capped on larger touch breakpoints). "Shop
// Products" delegates to the existing ProductsSlideMenu (EWM-294); the remaining
// nav items render as utility links (FAQ / Articles / Contact Us). The support
// contact section is anchored to the bottom via the Drawer's `footer` slot.
const MobileMenu = ({
  open,
  mainNavItems,
  onClose,
  onShopProducts,
  afterLeave,
  authActions,
  supportContact,
  productsMenuOpen,
  productsMenuId,
}: MobileMenuProps) => {
  const translate = useTranslations();

  const [shopProductsItem, ...rest] = mainNavItems;

  // `lg:hidden` is a desktop guard, not just styling: the shared Drawer (panel
  // + overlay) has no responsive constraint of its own, so without this any
  // code path that sets `open` on a desktop viewport would slide the mobile
  // drawer over the desktop nav. The Drawer portals its output (EWM-849), so
  // this can no longer be a wrapping div around it — it goes through
  // `wrapperClassName`, which the Drawer applies to the portaled root itself
  // (plain `className` only reaches the panel, not the overlay).
  return (
    <Drawer
      isOpen={open}
      direction="left"
      headline={translate('common.menu')}
      onClose={onClose}
      afterLeave={afterLeave}
      wrapperClassName="lg:hidden"
      // EWM-658 — Dan's v0 style guide insets this menu at px-4 (16px) for the
      // header, body and footer. The shared Drawer's default header is px-6
      // (24px); `!px-4` overrides it (classnames appends the hardcoded px-6
      // after headerClassName, so a plain px-4 loses the cascade).
      headerClassName="!px-4"
      footer={
        <SupportContactCard
          title={translate('customer-support.customer-support')}
          description={translate('customer-support.help-question')}
          phone={supportContact?.phone}
          email={supportContact?.email}
          hideIconBadge
          // Match the px-4 (16px) body inset; the card's default p-5 keeps its
          // 20px vertical padding, px-4 just overrides the horizontal.
          className="px-4"
        />
      }
    >
      <nav className="p-4">
        {shopProductsItem && (
          <button
            onClick={onShopProducts}
            aria-haspopup="dialog"
            aria-controls={productsMenuId}
            aria-expanded={productsMenuOpen}
            className="text-16 hover:text-primary flex w-full items-center justify-between border-b border-gray-100 py-3 text-gray-700"
          >
            <span>{shopProductsItem.label}</span>
            <ChevronRightIcon className="size-5" aria-hidden />
          </button>
        )}
        {rest.map((item) => (
          <Link
            key={item.label}
            href={item.href}
            block
            underlineOnHover={false}
            className="text-16 hover:text-primary block border-b border-gray-100 py-3 text-gray-700"
            onClick={onClose}
          >
            {item.label}
          </Link>
        ))}
        {authActions && <div className="mt-4 flex flex-col gap-3">{authActions}</div>}
      </nav>
    </Drawer>
  );
};

export default MobileMenu;
